import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Star, Quote, Lock, Shield, CreditCard, BadgeCheck, Calculator, Globe, Wand2, Sparkles, Search } from 'lucide-react';
import { useCountry } from '../contexts/CountryContext';

const COUNTRY_LEGAL_TERMS = {
  UK: {
    label: 'UK · England & Wales',
    terms: 'GDPR · Data Protection Act 2018 · MLR 2017 · Consumer Rights Act 2015',
    compliance_badge: 'GDPR Compliant',
  },
  IN: {
    label: 'India',
    terms: 'RERA Act 2016 · DPDPA 2023 · RBI FEMA · IT Rules 2021',
    compliance_badge: 'RERA-aware',
  },
  CA: {
    label: 'Canada',
    terms: 'PIPEDA · FINTRAC AML · Provincial REBBA / RESA conveyancing rules',
    compliance_badge: 'PIPEDA Compliant',
  },
};

export const TestimonialsSection = () => (
  <section className="py-24 px-4 sm:px-6 lg:px-8 border-b border-slate-100" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <span className="text-sm tracking-[0.3em] text-[#E07A3A] mb-4 block uppercase font-semibold">Client Stories</span>
        <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#1A1A1A] font-semibold">Trusted by Discerning Clients</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {[
          { name: 'Priya Sharma', role: 'Buyer — London', rating: 5, quote: 'The 360° virtual tour saved me two international flights. I found my Chelsea flat from Mumbai, and the AI valuation confirmed I was getting genuine value. The screening process gave me real confidence that the seller was legitimate.', highlight: '360° tours & AI valuation' },
          { name: 'James Whitfield', role: 'Seller — Manchester', quote: 'Within a week of listing, I had three verified buyers. Shopperden handled all the ID checks and document matching — I only spoke to serious, pre-screened prospects. Sold above asking price.', rating: 5, highlight: 'Sold above asking price' },
          { name: 'Adebayo & Funmi Okafor', role: 'Tenants — Birmingham', quote: 'As first-time renters in the UK, the step-by-step process made everything transparent. We knew exactly what fees were for, the admin verified our landlord, and we moved in stress-free within two weeks.', rating: 5, highlight: 'Moved in within 2 weeks' },
          { name: 'Raj Patel', role: 'Property Investor — Dubai', quote: 'The premium profit insights are exceptional. The AI flagged an undervalued property in Leeds that has already appreciated 12% since purchase. The global currency conversion made cross-border investing seamless.', rating: 5, highlight: '12% appreciation identified' },
          { name: 'Sophie Laurent', role: 'Seller — Edinburgh', quote: "My solicitor handled all communications through the platform's representative messaging feature. Everything was documented, professional, and GDPR-compliant. Exactly how property transactions should work.", rating: 5, highlight: 'Professional representative support' },
          { name: 'Michael & Sarah Chen', role: 'Buyers — Bristol', quote: 'We were nervous about buying sight-unseen from Singapore. The panoramic viewer, verified seller details, and the admin approval process gave us the assurance we needed. Our Bristol home is everything the listing promised.', rating: 5, highlight: 'Bought from Singapore confidently' },
        ].map((testimonial, index) => (
          <div key={testimonial.name} className="luxury-card p-8 flex flex-col justify-between group hover:shadow-lg transition-shadow duration-300 relative" data-testid={`testimonial-${index}`}>
            <div className="absolute top-3 right-3 border border-[#E07A3A]/30 px-2 py-0.5 bg-[#FEF3EC]">
              <span className="text-[#E07A3A] text-[9px] font-bold tracking-[0.2em] uppercase">Shopperden Test</span>
            </div>
            <div>
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: testimonial.rating }).map((_, i) => (
                  <Star key={`star-${testimonial.name}-${i}`} className="h-4 w-4 fill-[#E07A3A] text-[#E07A3A]" />
                ))}
              </div>
              <Quote className="h-8 w-8 text-[#E07A3A]/15 mb-3" />
              <p className="text-[#1A1A1A]/70 text-base sm:text-sm leading-relaxed mb-4 font-medium italic">"{testimonial.quote}"</p>
              <span className="inline-block text-xs tracking-wider uppercase bg-[#FEF3EC] text-[#E07A3A] px-3 py-1 font-semibold mb-4">{testimonial.highlight}</span>
            </div>
            <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
              <div className="w-10 h-10 bg-[#0F172A] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold text-sm">{testimonial.name.split(' ').map(n => n[0]).join('')}</span>
              </div>
              <div>
                <p className="text-[#1A1A1A] font-semibold text-sm">{testimonial.name}</p>
                <p className="text-[#1A1A1A]/50 text-xs font-medium">{testimonial.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const SavingsCalculator = ({ fees, calcPrice, setCalcPrice }) => (
  <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-100" data-testid="savings-calculator-section">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <span className="text-sm tracking-[0.3em] text-[#E07A3A] mb-4 block uppercase font-semibold">Save Thousands</span>
        <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#1A1A1A] font-semibold">
          How Much Do You Save<span className="italic text-[#E07A3A]"> Without </span>an Estate Agent?
        </h2>
        <p className="text-[#1A1A1A]/50 mt-4 max-w-2xl mx-auto text-base font-medium">
          Enter your property price and see the difference instantly. No hidden fees, no commission surprises.
        </p>
      </div>
      <div className="luxury-card p-8 sm:p-10">
        <div className="mb-8">
          <label className="text-xs text-slate-500 tracking-wider uppercase font-medium block mb-2">Enter Property Price ({fees.currency})</label>
          <input
            type="number"
            value={calcPrice}
            onChange={(e) => setCalcPrice(e.target.value)}
            placeholder={fees.country === 'IN' ? '5000000' : '250000'}
            className="w-full h-14 bg-[#F8FAFC] border border-slate-200 px-5 text-2xl font-['Playfair_Display'] text-[#1A1A1A] focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
            data-testid="savings-calc-input"
          />
        </div>
        {calcPrice && Number(calcPrice) > 0 && (() => {
          const price = Number(calcPrice);
          const agentFee1 = price * 0.01;
          const agentFee3 = price * 0.03;
          const shopperdenFee = fees.seller;
          const shopperdenRentFee = fees.sellerRent;
          const saving1 = agentFee1 - shopperdenFee;
          const saving3 = agentFee3 - shopperdenFee;
          return (
            <div className="space-y-6" data-testid="savings-results">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-red-50 p-6 border border-red-100">
                  <p className="text-xs text-red-500 tracking-wider uppercase font-bold mb-2">Traditional Estate Agent</p>
                  <p className="text-3xl font-['Playfair_Display'] text-red-600 font-semibold">
                    {fees.currency}{agentFee1.toLocaleString('en-GB')} — {fees.currency}{agentFee3.toLocaleString('en-GB')}
                  </p>
                  <p className="text-red-400 text-sm mt-1">1% — 3% commission</p>
                </div>
                <div className="bg-emerald-50 p-6 border border-emerald-200">
                  <p className="text-xs text-emerald-600 tracking-wider uppercase font-bold mb-2">Shopperden</p>
                  <div className="flex items-baseline gap-4">
                    <div>
                      <p className="text-3xl font-['Playfair_Display'] text-emerald-600 font-semibold">{fees.currency}{shopperdenFee}</p>
                      <p className="text-emerald-500 text-xs mt-1">Selling — flat fee</p>
                    </div>
                    <span className="text-emerald-300 text-lg">|</span>
                    <div>
                      <p className="text-3xl font-['Playfair_Display'] text-emerald-600 font-semibold">{fees.currency}{shopperdenRentFee}</p>
                      <p className="text-emerald-500 text-xs mt-1">Renting — reduced</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-[#0F172A] p-6 text-center">
                <p className="text-white/50 text-xs tracking-wider uppercase font-bold mb-2">You Could Save</p>
                <p className="text-4xl font-['Playfair_Display'] text-[#E07A3A] font-semibold">
                  {fees.currency}{saving1.toLocaleString('en-GB')} — {fees.currency}{saving3.toLocaleString('en-GB')}
                </p>
                <p className="text-white/40 text-sm mt-2">By choosing Shopperden over a traditional agent</p>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  </section>
);

export const TrustSeals = ({ liveStats }) => (
  <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#F8FAFC] border-b border-slate-100" data-testid="trust-seals-section">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-10">
        <span className="text-sm tracking-[0.3em] text-[#E07A3A] mb-3 block uppercase font-semibold">Your Security</span>
        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#1A1A1A] font-semibold">Trusted & Protected</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="text-center p-6 bg-white border border-slate-100 hover:border-[#E07A3A]/20 transition-colors" data-testid="trust-ssl">
          <Lock className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#1A1A1A]">SSL Secured</p>
          <p className="text-xs text-slate-400 mt-1">256-bit encryption</p>
        </div>
        <div className="text-center p-6 bg-white border border-slate-100 hover:border-[#E07A3A]/20 transition-colors" data-testid="trust-gdpr">
          <Shield className="h-8 w-8 text-blue-600 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#1A1A1A]">GDPR Compliant</p>
          <p className="text-xs text-slate-400 mt-1">Data protection assured</p>
        </div>
        <div className="text-center p-6 bg-white border border-slate-100 hover:border-[#E07A3A]/20 transition-colors" data-testid="trust-stripe">
          <CreditCard className="h-8 w-8 text-purple-600 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#1A1A1A]">Stripe Protected</p>
          <p className="text-xs text-slate-400 mt-1">Secure payments</p>
        </div>
        <div className="text-center p-6 bg-white border border-slate-100 hover:border-[#E07A3A]/20 transition-colors" data-testid="trust-verified">
          <BadgeCheck className="h-8 w-8 text-[#E07A3A] mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#1A1A1A]">ID Verified Users</p>
          <p className="text-xs text-slate-400 mt-1">{liveStats.verified_users > 0 ? `${liveStats.verified_users}+ verified` : 'All users checked'}</p>
        </div>
      </div>
    </div>
  </section>
);

export const LandingCTA = () => (
  <section className="py-24 px-4 sm:px-6 lg:px-8 border-t border-slate-100">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl text-[#1A1A1A] mb-6 font-semibold">
        Begin Your Journey to<span className="italic text-[#E07A3A]"> Exceptional </span>Living
      </h2>
      <p className="text-[#1A1A1A]/60 text-lg sm:text-xl mb-10 max-w-2xl mx-auto font-medium">
        Join an exclusive community of discerning buyers and sellers who trust us with their most valuable investments.
      </p>
      <Link to="/register">
        <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-12 py-6 text-sm tracking-widest uppercase blue-glow" data-testid="cta-get-started-btn">
          Get Started <ArrowRight className="ml-3 h-4 w-4" />
        </Button>
      </Link>
    </div>
  </section>
);

export const LandingFooter = () => {
  const { country } = useCountry();
  const legal = COUNTRY_LEGAL_TERMS[country] || COUNTRY_LEGAL_TERMS.UK;
  return (
  <footer className="bg-[#0A0A0C] py-12 px-4 sm:px-6 lg:px-8 border-t-2 border-[#8A7124]" data-testid="landing-footer">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-wrap justify-center gap-6 mb-8 pb-8 border-b border-white/10" data-testid="trust-bar">
        <div className="flex items-center gap-2 text-white/50 text-xs tracking-wider">
          <Lock className="h-3.5 w-3.5 text-[#22C55E]" />
          <span>SSL Secured</span>
        </div>
        <div className="flex items-center gap-2 text-white/50 text-xs tracking-wider" data-testid="footer-compliance-badge">
          <Shield className="h-3.5 w-3.5 text-[#D4AF37]" />
          <span>{legal.compliance_badge}</span>
        </div>
        <div className="flex items-center gap-2 text-white/50 text-xs tracking-wider">
          <CreditCard className="h-3.5 w-3.5 text-[#A1A1AA]" />
          <span>Stripe Protected</span>
        </div>
        <div className="flex items-center gap-2 text-white/50 text-xs tracking-wider">
          <Globe className="h-3.5 w-3.5 text-[#D4AF37]" />
          <span>UK · India · Canada</span>
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border border-[#D4AF37] flex items-center justify-center">
            <span className="text-[#D4AF37] font-['Playfair_Display'] text-sm">S</span>
          </div>
          <span className="font-['Playfair_Display'] text-lg text-white tracking-wide">SHOPPER<span className="text-[#D4AF37]">DEN</span></span>
        </div>
        <div className="flex items-center gap-4 flex-wrap justify-center">
          <p className="text-white/50 text-sm tracking-wider">© 2026 Shopperden Ltd. Registered in England &amp; Wales. AI Real Estate Network.</p>
          <Link to="/contact" className="text-white/50 text-sm hover:text-[#D4AF37] underline tracking-wider" data-testid="footer-contact-link">Contact</Link>
          <Link to="/for-agents" className="text-white/50 text-sm hover:text-[#D4AF37] underline tracking-wider" data-testid="footer-for-agents-link">For Estate Agents</Link>
          <Link to="/in-the-news" className="text-white/50 text-sm hover:text-[#D4AF37] underline tracking-wider" data-testid="footer-press-link">In the News</Link>
          <Link to="/affiliate" className="text-emerald-300 text-sm hover:text-emerald-200 font-semibold underline tracking-wider" data-testid="footer-affiliate-link">Earn 25% — Free Affiliate Sign-Up</Link>
          <Link to="/blog" className="text-white/50 text-sm hover:text-[#D4AF37] underline tracking-wider" data-testid="footer-blog-link">Blog</Link>
          <Link to="/terms" className="text-white/50 text-sm hover:text-white/80 underline tracking-wider">Terms &amp; Conditions</Link>
        </div>
      </div>
      <div className="mt-4 flex flex-col items-center gap-2" data-testid="footer-dynamic-legal">
        <p className="text-[#D4AF37]/70 text-[11px] tracking-[0.2em] uppercase">Operating under {legal.label} law · {legal.terms}</p>
        <div className="border border-[#D4AF37]/30 bg-black/40 px-4 py-3 max-w-3xl mt-2">
          <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold mb-1.5">SHOPPERDEN IS NOT AN ESTATE AGENT</p>
          <p className="text-white/70 text-xs leading-relaxed text-center">
            This platform is an <b>automated intermediary</b>. It does not verify the title of properties,
            the identity of users, or the legality of listings. The fee charged is strictly for AI-driven
            data processing and digital advertising visibility, not for real estate brokerage or consultancy.
            <b className="text-white/90"> 100% of matching is done by AI — no staff provide consultancy.</b>
            Listings without a valid <b>RERA number</b> are at the buyer's own risk; our AI simply sorts data provided by the user.
            Independent professional advice (solicitor, surveyor, RERA-authority verification) is the user's responsibility before acting.
          </p>
        </div>
      </div>
    </div>
  </footer>
  );
};

export const WhyShopperdenSection = ({ whyContent, fees }) => {
  if (!whyContent || whyContent.enabled === false) return null;
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#F8FAFC]" data-testid="why-shopperden-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] font-semibold">{whyContent.headline}</h2>
          <p className="text-[#E07A3A] font-['Playfair_Display'] text-lg sm:text-xl italic mt-2 font-semibold">{whyContent.headline_accent}</p>
        </div>
        {whyContent.points && whyContent.points.length > 0 && (
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-20">
            {whyContent.points.map((point, i) => {
              const bgColors = ['bg-emerald-50', 'bg-blue-50', 'bg-[#FEF3EC]'];
              const textColors = ['text-emerald-600', 'text-blue-600', 'text-[#E07A3A]'];
              const icons = [<CreditCard key="ic1" className="h-7 w-7" />, <Shield key="ic2" className="h-7 w-7" />, <Search key="ic3" className="h-7 w-7" />];
              const dynamicHighlight = i === 0 ? `Sell: ${fees.currency}${fees.seller} · Rent: ${fees.currency}${fees.sellerRent}` : point.highlight;
              return (
                <div key={point.title} className="text-center p-8" data-testid={`why-point-${i}`}>
                  <div className={`w-16 h-16 mx-auto mb-5 ${bgColors[i % 3]} ${textColors[i % 3]} flex items-center justify-center rounded-full`}>{icons[i % 3]}</div>
                  {dynamicHighlight && (
                    <span className={`inline-block text-sm sm:text-xs tracking-[0.2em] uppercase font-bold mb-3 ${textColors[i % 3]}`}>{dynamicHighlight}</span>
                  )}
                  <h3 className="font-['Playfair_Display'] text-xl sm:text-2xl text-[#0F172A] font-semibold mb-3">{point.title}</h3>
                  <p className="text-[#1A1A1A]/50 text-base sm:text-sm leading-relaxed font-medium">{point.description}</p>
                </div>
              );
            })}
          </div>
        )}
        {whyContent.ai_tools && (
          <div className="mb-16">
            <div className="bg-[#0F172A] p-8 sm:p-12 relative overflow-hidden">
              <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#E07A3A]/20 flex items-center justify-center"><Wand2 className="h-5 w-5 text-[#E07A3A]" /></div>
                  <span className="text-[#E07A3A] text-xs tracking-[0.3em] uppercase font-bold">Powered by AI</span>
                </div>
                <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-white font-semibold mb-3">{whyContent.ai_tools.headline}</h3>
                <p className="text-white/50 text-base sm:text-sm max-w-2xl mb-10 leading-relaxed font-medium">{whyContent.ai_tools.description}</p>
                <div className="grid sm:grid-cols-3 gap-6">
                  {whyContent.ai_tools.features?.map((feature, i) => {
                    const featureIcons = [<Wand2 key="fi1" className="h-5 w-5" />, <Sparkles key="fi2" className="h-5 w-5" />, <Search key="fi3" className="h-5 w-5" />];
                    return (
                      <div key={feature.title} className="border border-white/10 p-6 bg-white/[0.02]" data-testid={`ai-tool-${i}`}>
                        <div className="text-[#E07A3A] mb-4">{featureIcons[i % 3]}</div>
                        <h4 className="text-white font-semibold text-base sm:text-sm mb-2">{feature.title}</h4>
                        <p className="text-white/40 text-sm sm:text-xs leading-relaxed font-medium">{feature.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="text-center mt-14">
          <Link to="/register">
            <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-12 py-7 text-base tracking-widest uppercase font-semibold shadow-lg shadow-[#E07A3A]/20" data-testid="why-cta-btn">
              {whyContent.cta_text} <ArrowRight className="ml-3 h-5 w-5" />
            </Button>
          </Link>
          <p className="text-[#1A1A1A]/30 text-xs mt-4 tracking-wider font-medium">{whyContent.cta_subtitle}</p>
        </div>
      </div>
    </section>
  );
};

export const AdBannersSection = ({ adBanners, adsenseConfig, trackBannerClick }) => {
  if (!(adBanners.length > 0 || (adsenseConfig?.enabled && adsenseConfig?.publisher_id))) return null;
  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white" data-testid="ad-banners-section">
      <div className="max-w-7xl mx-auto">
        {adBanners.length > 0 && (
          <div className={`grid gap-4 ${adBanners.length === 1 ? 'grid-cols-1 max-w-3xl mx-auto' : adBanners.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3'}`}>
            {adBanners.map((banner) => (
              <a key={banner.id} href={banner.link_url} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackBannerClick(banner.id)} className="block group overflow-hidden border border-slate-100 hover:border-[#E07A3A]/30 transition-all duration-300 hover:shadow-md" data-testid={`ad-banner-${banner.id}`}>
                {banner.image_url && <img src={banner.image_url} alt={banner.title || 'Partner'} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />}
                {banner.title && (
                  <div className="p-3 bg-white">
                    <p className="text-sm text-slate-600 font-medium truncate">{banner.title}</p>
                    <span className="text-[10px] text-slate-300 uppercase tracking-widest">Sponsored</span>
                  </div>
                )}
              </a>
            ))}
          </div>
        )}
        {adsenseConfig?.enabled && adsenseConfig?.publisher_id && (
          <div className="mt-4 text-center" data-testid="adsense-slot">
            <ins className="adsbygoogle" style={{ display: 'block' }} data-ad-client={adsenseConfig.publisher_id} data-ad-slot={adsenseConfig.slots?.landing || ''} data-ad-format="auto" data-full-width-responsive="true" />
          </div>
        )}
      </div>
    </section>
  );
};
